import { useState } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Check,
  X,
  Eye,
  EyeOff,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useAdminCourses } from '@/services/admin/courses.service'
import { useAdminModules } from '@/services/admin/modules.service'
import { useAdminEpisodes } from '@/services/admin/episodes.service'
import {
  useAdminQuizByEpisode,
  useCreateQuiz,
  useUpdateQuiz,
  useUpdateQuizStatus,
  useDeleteQuiz,
  useCreateQuestion,
  useUpdateQuestion,
  useDeleteQuestion,
  type Quiz,
  type QuizQuestion,
  type QuestionType,
} from '@/services/admin/quiz.service'
import { useAuth } from '@/hooks/use-auth'
import { canManageCourseResource } from '@/types/auth'

// ─── Question Form Dialog ────────────────────────────────────────────────────

interface QuestionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  quizId: number
  question?: QuizQuestion
}

const emptyOption = (order: number) => ({ text: '', isCorrect: false, order })

function QuestionDialog({ open, onOpenChange, quizId, question }: QuestionDialogProps) {
  const { toast } = useToast()
  const createQuestion = useCreateQuestion()
  const updateQuestion = useUpdateQuestion()

  const isEditing = !!question

  const [text, setText] = useState(question?.text || '')
  const [type, setType] = useState<QuestionType>(question?.type || 'multiple_choice')
  const [explanation, setExplanation] = useState(question?.explanation || '')
  const [options, setOptions] = useState(
    question?.options || [emptyOption(1), emptyOption(2), emptyOption(3), emptyOption(4)]
  )

  const handleTypeChange = (val: QuestionType) => {
    setType(val)
    if (val === 'true_false') {
      setOptions([
        { text: 'Verdadeiro', isCorrect: true, order: 1 },
        { text: 'Falso', isCorrect: false, order: 2 },
      ])
    } else {
      setOptions(
        question?.options || [emptyOption(1), emptyOption(2), emptyOption(3), emptyOption(4)]
      )
    }
  }

  const setOptionText = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? { ...o, text: value } : o)))
  }

  const setCorrect = (index: number) => {
    setOptions((prev) => prev.map((o, i) => ({ ...o, isCorrect: i === index })))
  }

  const handleSave = async () => {
    if (!text.trim())
      return toast({ title: 'Informe o enunciado da questão', variant: 'destructive' })
    if (!options.some((o) => o.isCorrect))
      return toast({ title: 'Marque uma opção como correta', variant: 'destructive' })
    if (options.some((o) => !o.text.trim()))
      return toast({ title: 'Preencha todas as opções', variant: 'destructive' })

    try {
      if (isEditing && question) {
        await updateQuestion.mutateAsync({
          id: question.id,
          data: { text, type, explanation, options },
        })
        toast({ title: 'Questão atualizada!' })
      } else {
        await createQuestion.mutateAsync({ quizId, text, type, explanation, options })
        toast({ title: 'Questão criada!' })
      }
      onOpenChange(false)
    } catch {
      toast({ title: 'Erro ao salvar questão', variant: 'destructive' })
    }
  }

  const isPending = createQuestion.isPending || updateQuestion.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Questão' : 'Nova Questão'}</DialogTitle>
          <DialogDescription>Preencha o enunciado e as opções de resposta.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Tipo de questão</Label>
            <Select value={type} onValueChange={(v) => handleTypeChange(v as QuestionType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multiple_choice">Múltipla escolha</SelectItem>
                <SelectItem value="true_false">Verdadeiro / Falso</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Enunciado</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Digite o enunciado da questão..."
              rows={3}
            />
          </div>

          <div>
            <Label className="mb-2 block">
              Opções{' '}
              {type === 'multiple_choice' && (
                <span className="text-muted-foreground text-xs">
                  (clique no ✓ para marcar a correta)
                </span>
              )}
            </Label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCorrect(i)}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      opt.isCorrect
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <Input
                    value={opt.text}
                    onChange={(e) => setOptionText(i, e.target.value)}
                    placeholder={`Opção ${i + 1}`}
                    disabled={type === 'true_false'}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>
              Explicação da resposta{' '}
              <span className="text-muted-foreground text-xs">
                (opcional — exibida ao aluno após confirmar)
              </span>
            </Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Ex: A resposta correta é X porque..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar questão'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Quiz Form Dialog ────────────────────────────────────────────────────────

interface QuizFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  episodeId: number
  quiz?: Quiz
}

function QuizFormDialog({ open, onOpenChange, episodeId, quiz }: QuizFormDialogProps) {
  const { toast } = useToast()
  const createQuiz = useCreateQuiz()
  const updateQuiz = useUpdateQuiz()

  const [title, setTitle] = useState(quiz?.title || '')
  const [description, setDescription] = useState(quiz?.description || '')

  const handleSave = async () => {
    if (!title.trim()) return toast({ title: 'Informe o título do quiz', variant: 'destructive' })
    try {
      if (quiz) {
        await updateQuiz.mutateAsync({ id: quiz.id, data: { title, description } })
        toast({ title: 'Quiz atualizado!' })
      } else {
        await createQuiz.mutateAsync({ episodeId, title, description })
        toast({ title: 'Quiz criado!' })
      }
      onOpenChange(false)
    } catch {
      toast({ title: 'Erro ao salvar quiz', variant: 'destructive' })
    }
  }

  const isPending = createQuiz.isPending || updateQuiz.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{quiz ? 'Editar Quiz' : 'Novo Quiz'}</DialogTitle>
          <DialogDescription>Configure o quiz para este episódio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Quiz do Episódio 1.1"
            />
          </div>
          <div>
            <Label>
              Descrição <span className="text-muted-foreground text-xs">(opcional)</span>
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrição do quiz..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? 'Salvando...' : quiz ? 'Salvar' : 'Criar quiz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function QuizManagement() {
  const { toast } = useToast()
  const { user } = useAuth()
  const { data: courses, isLoading: coursesLoading } = useAdminCourses({ status: 'published' })
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>()
  const [selectedModuleId, setSelectedModuleId] = useState<number | undefined>()
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<number | undefined>()
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null)

  const [quizFormOpen, setQuizFormOpen] = useState(false)
  const [questionFormOpen, setQuestionFormOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | undefined>()
  const [deleteQuizId, setDeleteQuizId] = useState<number | null>(null)
  const [deleteQuestionId, setDeleteQuestionId] = useState<number | null>(null)

  const { data: modules, isLoading: modulesLoading } = useAdminModules(selectedCourseId)
  const { data: episodes, isLoading: episodesLoading } = useAdminEpisodes(
    selectedModuleId ? { moduleId: selectedModuleId } : undefined
  )
  const { data: quiz, isLoading: quizLoading } = useAdminQuizByEpisode(selectedEpisodeId)

  const deleteQuiz = useDeleteQuiz()
  const deleteQuestion = useDeleteQuestion()
  const updateQuizStatus = useUpdateQuizStatus()

  const selectedCourse = courses?.find((c) => c.id === selectedCourseId)
  const canManage = canManageCourseResource(user?.role, selectedCourse?.companyId)

  const handleDeleteQuiz = async () => {
    if (!deleteQuizId) return
    try {
      await deleteQuiz.mutateAsync(deleteQuizId)
      toast({ title: 'Quiz excluído.' })
    } catch {
      toast({ title: 'Erro ao excluir quiz', variant: 'destructive' })
    }
    setDeleteQuizId(null)
  }

  const handleToggleStatus = async () => {
    if (!quiz) return
    const newStatus = quiz.status === 'published' ? 'draft' : 'published'
    try {
      await updateQuizStatus.mutateAsync({ id: quiz.id, status: newStatus })
      toast({ title: newStatus === 'published' ? 'Quiz publicado!' : 'Quiz despublicado.' })
    } catch {
      toast({ title: 'Erro ao alterar status do quiz', variant: 'destructive' })
    }
  }

  const handleDeleteQuestion = async () => {
    if (!deleteQuestionId) return
    try {
      await deleteQuestion.mutateAsync(deleteQuestionId)
      toast({ title: 'Questão excluída.' })
    } catch {
      toast({ title: 'Erro ao excluir questão', variant: 'destructive' })
    }
    setDeleteQuestionId(null)
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Gerenciamento de Quizzes
          </CardTitle>
          <CardDescription>
            Selecione o episódio para criar ou editar o quiz correspondente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label>Curso</Label>
              <Select
                value={selectedCourseId?.toString() || ''}
                onValueChange={(v) => {
                  setSelectedCourseId(Number(v))
                  setSelectedModuleId(undefined)
                  setSelectedEpisodeId(undefined)
                }}
                disabled={coursesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={coursesLoading ? 'Carregando cursos...' : 'Selecionar curso'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {courses?.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhum curso publicado
                    </div>
                  )}
                  {courses?.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Módulo</Label>
              <Select
                value={selectedModuleId?.toString() || ''}
                onValueChange={(v) => {
                  setSelectedModuleId(Number(v))
                  setSelectedEpisodeId(undefined)
                }}
                disabled={!selectedCourseId || modulesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={modulesLoading ? 'Carregando módulos...' : 'Selecionar módulo'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {modules?.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhum módulo encontrado
                    </div>
                  )}
                  {modules?.map((m) => (
                    <SelectItem key={m.id} value={m.id.toString()}>
                      {m.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Episódio</Label>
              <Select
                value={selectedEpisodeId?.toString() || ''}
                onValueChange={(v) => setSelectedEpisodeId(Number(v))}
                disabled={!selectedModuleId || episodesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      episodesLoading ? 'Carregando episódios...' : 'Selecionar episódio'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {episodes?.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhum episódio encontrado
                    </div>
                  )}
                  {episodes?.map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()}>
                      Ep. {e.number} – {e.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quiz Editor */}
      {selectedEpisodeId && (
        <Card className="glass-card border-border/50">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle>Quiz do Episódio</CardTitle>
              <CardDescription>
                {quizLoading ? 'Carregando...' : quiz ? quiz.title : 'Nenhum quiz criado ainda.'}
              </CardDescription>
            </div>
            {quiz ? (
              <div className="flex items-center gap-2">
                <Badge variant={quiz.status === 'published' ? 'default' : 'secondary'}>
                  {quiz.status === 'published' ? 'Publicado' : 'Rascunho'}
                </Badge>
                {canManage && (
                  <>
                    <Button
                      size="sm"
                      variant={quiz.status === 'published' ? 'outline' : 'default'}
                      onClick={handleToggleStatus}
                      disabled={updateQuizStatus.isPending}
                    >
                      {quiz.status === 'published' ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" /> Despublicar
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" /> Publicar
                        </>
                      )}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setQuizFormOpen(true)}>
                      <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setDeleteQuizId(quiz.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Excluir
                    </Button>
                  </>
                )}
                {!canManage && (
                  <span className="text-xs text-muted-foreground">Somente leitura</span>
                )}
              </div>
            ) : canManage ? (
              <Button size="sm" onClick={() => setQuizFormOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> Criar Quiz
              </Button>
            ) : null}
          </CardHeader>

          {quiz && (
            <CardContent className="space-y-4">
              {quiz.description && (
                <p className="text-sm text-muted-foreground">{quiz.description}</p>
              )}

              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">
                  Questões{' '}
                  <Badge variant="secondary" className="ml-2">
                    {quiz.questions.length}
                  </Badge>
                </h3>
                {canManage && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingQuestion(undefined)
                      setQuestionFormOpen(true)
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Nova Questão
                  </Button>
                )}
              </div>

              {quiz.questions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <HelpCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Nenhuma questão ainda. Adicione a primeira!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {quiz.questions.map((q, idx) => (
                    <div key={q.id} className="border border-border rounded-lg overflow-hidden">
                      <div
                        role="button"
                        tabIndex={0}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/20 transition-colors cursor-pointer"
                        onClick={() => setExpandedQuestion(expandedQuestion === q.id ? null : q.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setExpandedQuestion(expandedQuestion === q.id ? null : q.id)
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="text-sm font-medium text-foreground">{q.text}</span>
                          <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                            {q.type === 'multiple_choice' ? 'Múltipla escolha' : 'V/F'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {canManage && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditingQuestion(q)
                                  setQuestionFormOpen(true)
                                }}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDeleteQuestionId(q.id)
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          {expandedQuestion === q.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </div>

                      {expandedQuestion === q.id && (
                        <div className="px-4 pb-4 pt-1 border-t border-border space-y-2">
                          {q.options.map((opt) => (
                            <div
                              key={opt.id}
                              className={`flex items-center gap-3 p-2 rounded-md ${opt.isCorrect ? 'bg-primary/10 border border-primary/30' : 'bg-muted/20'}`}
                            >
                              {opt.isCorrect ? (
                                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                              ) : (
                                <X className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              )}
                              <span
                                className={`text-sm ${opt.isCorrect ? 'text-primary font-medium' : 'text-muted-foreground'}`}
                              >
                                {opt.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Dialogs */}
      {selectedEpisodeId && (
        <QuizFormDialog
          open={quizFormOpen}
          onOpenChange={setQuizFormOpen}
          episodeId={selectedEpisodeId}
          quiz={quiz || undefined}
        />
      )}

      {quiz && (
        <QuestionDialog
          key={editingQuestion?.id ?? 'new'}
          open={questionFormOpen}
          onOpenChange={setQuestionFormOpen}
          quizId={quiz.id}
          question={editingQuestion}
        />
      )}

      <AlertDialog open={!!deleteQuizId} onOpenChange={() => setDeleteQuizId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir quiz?</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as questões e opções serão removidas permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuiz}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteQuestionId} onOpenChange={() => setDeleteQuestionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir questão?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuestion}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
